import React, {useState, useEffect} from 'react';
import useApiStable from '../../utils/useApiStable';
import '../../styles/AppointmentList.css';

const AppointmentList = () => {
    const api = useApiStable();
    const [appointments, setAppointments] = useState([]);
    const [groups, setGroups] = useState([]);
    const [persons, setPersons] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        startTime: '',
        durationMinutes: 60,
        location: '',
        groupIds: [],
        participantIds: [],
        organizerId: '',
        status: 'SCHEDULED',
        type: 'MEETING',
        notes: ''
    });

    const getPersonName = (person) => {
        if (!person) return '';
        if (person.name) return person.name;
        const first = person.firstName || '';
        const last = person.lastName || '';
        return `${first} ${last}`.trim();
    };

    useEffect(() => {
        fetchAppointments();
        fetchGroups();
        fetchPersons();
    }, [api]);

    const fetchAppointments = async () => {
        try {
            const data = await api.get('/appointments');
            setAppointments(data);
        } catch (error) {
            console.error('Fehler beim Laden der Termine:', error);
        }
    };

    const fetchGroups = async () => {
        try {
            const data = await api.get('/person-groups');
            setGroups(data);
        } catch (error) {
            console.error('Fehler beim Laden der Gruppen:', error);
        }
    };

    const fetchPersons = async () => {
        try {
            const data = await api.get('/persons');
            setPersons(data);
        } catch (error) {
            console.error('Fehler beim Laden der Personen:', error);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const handleMultiSelectChange = (e, field) => {
        const options = e.target.options;
        const values = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                values.push(parseInt(options[i].value));
            }
        }
        setFormData({...formData, [field]: values});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                organizerId: formData.organizerId ? parseInt(formData.organizerId) : null
            };

            if (editingAppointment) {
                await api.put(`/appointments/${editingAppointment.id}`, submitData);
            } else {
                await api.post('/appointments', submitData);
            }

            resetForm();
            fetchAppointments();
        } catch (error) {
            console.error('Fehler beim Speichern des Termins:', error);
            alert('Fehler beim Speichern des Termins');
        }
    };

    const handleEdit = (appointment) => {
        setEditingAppointment(appointment);
        setFormData({
            title: appointment.title || '',
            description: appointment.description || '',
            startTime: appointment.startTime ? appointment.startTime.substring(0, 16) : '',
            durationMinutes: appointment.durationMinutes || 60,
            location: appointment.location || '',
            groupIds: appointment.groups ? appointment.groups.map(g => g.id) : [],
            participantIds: appointment.participants ? appointment.participants.map(p => p.id) : [],
            organizerId: appointment.organizer ? appointment.organizer.id : '',
            status: appointment.status || 'SCHEDULED',
            type: appointment.type || 'MEETING',
            notes: appointment.notes || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Möchten Sie diesen Termin wirklich löschen?')) {
            try {
                await api.delete(`/appointments/${id}`);
                fetchAppointments();
            } catch (error) {
                console.error('Fehler beim Löschen des Termins:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            startTime: '',
            durationMinutes: 60,
            location: '',
            groupIds: [],
            participantIds: [],
            organizerId: '',
            status: 'SCHEDULED',
            type: 'MEETING',
            notes: ''
        });
        setEditingAppointment(null);
        setShowForm(false);
    };

    const filterAppointmentsByGroup = async (groupId) => {
        setSelectedGroup(groupId);
        if (groupId) {
            try {
                const data = await api.get(`/appointments/group/${groupId}`);
                setAppointments(data);
            } catch (error) {
                console.error('Fehler beim Filtern der Termine:', error);
            }
        } else {
            fetchAppointments();
        }
    };

    const roundToNearest15Minutes = (date) => {
        const minutes = date.getMinutes();
        const roundedMinutes = Math.round(minutes / 15) * 15;
        date.setMinutes(roundedMinutes);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    };

    const handleDateTimeChange = (e) => {
        let value = e.target.value;
        if (value) {
            // Konvertiere zu Date-Objekt und runde auf 15 Minuten
            const date = new Date(value);
            const rounded = roundToNearest15Minutes(date);
            // Konvertiere zurück zu datetime-local Format
            const year = rounded.getFullYear();
            const month = String(rounded.getMonth() + 1).padStart(2, '0');
            const day = String(rounded.getDate()).padStart(2, '0');
            const hours = String(rounded.getHours()).padStart(2, '0');
            const minutes = String(rounded.getMinutes()).padStart(2, '0');
            value = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        setFormData({...formData, startTime: value});
    };

    const calculateEndTime = () => {
        if (!formData.startTime || !formData.durationMinutes) return '';
        const start = new Date(formData.startTime);
        const end = new Date(start.getTime() + formData.durationMinutes * 60000);
        return end.toLocaleString('de-DE');
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return '';
        return new Date(dateTime).toLocaleString('de-DE');
    };

    const getStatusText = (status) => {
        const statusMap = {
            'SCHEDULED': 'Geplant',
            'CONFIRMED': 'Bestätigt',
            'CANCELLED': 'Abgesagt',
            'COMPLETED': 'Abgeschlossen'
        };
        return statusMap[status] || status;
    };

    const getTypeText = (type) => {
        const typeMap = {
            'MEETING': 'Besprechung',
            'TRAINING': 'Training',
            'TOURNAMENT': 'Turnier',
            'SOCIAL': 'Soziales',
            'OTHER': 'Sonstiges'
        };
        return typeMap[type] || type;
    };

    return (
        <div className="appointment-list-container">
            <h2>Terminverwaltung</h2>

            <div className="appointment-controls">
                <div className="button-group">
                    <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
                        {showForm ? 'Liste anzeigen' : 'Neuer Termin'}
                    </button>
                </div>

                <div className="filter-group">
                    <label>Nach Gruppe filtern:</label>
                    <select
                        value={selectedGroup}
                        onChange={(e) => filterAppointmentsByGroup(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Alle Termine</option>
                        {groups.map(group => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {showForm && (
                <div className="appointment-form">
                    <h3>{editingAppointment ? 'Termin bearbeiten' : 'Neuer Termin'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Titel *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label>Typ</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="form-control"
                                >
                                    <option value="MEETING">Besprechung</option>
                                    <option value="TRAINING">Training</option>
                                    <option value="TOURNAMENT">Turnier</option>
                                    <option value="SOCIAL">Soziales</option>
                                    <option value="OTHER">Sonstiges</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Beschreibung</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="3"
                                maxLength={1000}
                                className="form-control"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Startdatum und -zeit *</label>
                                <input
                                    type="datetime-local"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleDateTimeChange}
                                    step="900"
                                    required
                                    className="form-control"
                                />
                                <small>Zeit wird auf 15-Minuten-Intervalle gerundet</small>
                            </div>

                            <div className="form-group">
                                <label>Dauer *</label>
                                <select
                                    name="durationMinutes"
                                    value={formData.durationMinutes}
                                    onChange={handleInputChange}
                                    required
                                    className="form-control"
                                >
                                    <option value={30}>30 Minuten</option>
                                    <option value={60}>1 Stunde</option>
                                    <option value={90}>1,5 Stunden</option>
                                    <option value={120}>2 Stunden</option>
                                    <option value={150}>2,5 Stunden</option>
                                    <option value={180}>3 Stunden</option>
                                    <option value={210}>3,5 Stunden</option>
                                    <option value={240}>4 Stunden</option>
                                    <option value={270}>4,5 Stunden</option>
                                    <option value={300}>5 Stunden</option>
                                    <option value={330}>5,5 Stunden</option>
                                    <option value={360}>6 Stunden</option>
                                </select>
                            </div>
                        </div>

                        {formData.startTime && formData.durationMinutes && (
                            <div className="form-group">
                                <label>Endzeit (automatisch berechnet)</label>
                                <input
                                    type="text"
                                    value={calculateEndTime()}
                                    disabled
                                    className="form-control disabled-field"
                                />
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>Ort</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label>Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className="form-control"
                                >
                                    <option value="SCHEDULED">Geplant</option>
                                    <option value="CONFIRMED">Bestätigt</option>
                                    <option value="CANCELLED">Abgesagt</option>
                                    <option value="COMPLETED">Abgeschlossen</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Gruppen</label>
                                <select
                                    multiple
                                    value={formData.groupIds}
                                    onChange={(e) => handleMultiSelectChange(e, 'groupIds')}
                                    className="form-control multi-select"
                                    size="5"
                                >
                                    {groups.map(group => (
                                        <option key={group.id} value={group.id}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                                <small>Strg/Cmd gedrückt halten für Mehrfachauswahl</small>
                            </div>

                            <div className="form-group">
                                <label>Teilnehmer</label>
                                <select
                                    multiple
                                    value={formData.participantIds}
                                    onChange={(e) => handleMultiSelectChange(e, 'participantIds')}
                                    className="form-control multi-select"
                                    size="5"
                                >
                                    {persons.map(person => (
                                        <option key={person.id} value={person.id}>
                                            {getPersonName(person)}
                                        </option>
                                    ))}
                                </select>
                                <small>Strg/Cmd gedrückt halten für Mehrfachauswahl</small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Organisator</label>
                            <select
                                name="organizerId"
                                value={formData.organizerId}
                                onChange={handleInputChange}
                                className="form-control"
                            >
                                <option value="">Bitte auswählen</option>
                                {persons.map(person => (
                                    <option key={person.id} value={person.id}>
                                        {getPersonName(person)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Notizen</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                rows="3"
                                maxLength={255}
                                className="form-control"
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn btn-success">Speichern</button>
                            <button type="button" onClick={resetForm} className="btn btn-secondary">
                                Abbrechen
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {!showForm && (
                <div className="appointment-list">
                    {appointments.length === 0 ? (
                        <p className="no-data">Keine Termine vorhanden</p>
                    ) : (
                        <div className="appointments-grid">
                            {appointments.map(appointment => (
                                <div key={appointment.id} className="appointment-card">
                                    <div className="appointment-header">
                                        <h3>{appointment.title}</h3>
                                        <span className={`status-badge status-${appointment.status?.toLowerCase()}`}>
                                            {getStatusText(appointment.status)}
                                        </span>
                                    </div>

                                    <div className="appointment-body">
                                        <p><strong>Typ:</strong> {getTypeText(appointment.type)}</p>

                                        {appointment.description && (
                                            <p><strong>Beschreibung:</strong> {appointment.description}</p>
                                        )}

                                        <p><strong>Start:</strong> {formatDateTime(appointment.startTime)}</p>

                                        {appointment.endDateTime && (
                                            <p><strong>Ende:</strong> {formatDateTime(appointment.endDateTime)}</p>
                                        )}

                                        {appointment.location && (
                                            <p><strong>Ort:</strong> {appointment.location}</p>
                                        )}

                                        {appointment.organizer && (
                                            <p>
                                                <strong>Organisator:</strong> {getPersonName(appointment.organizer)}
                                            </p>
                                        )}

                                        {appointment.groups && appointment.groups.length > 0 && (
                                            <div className="appointment-groups">
                                                <strong>Gruppen:</strong>
                                                <div className="tags">
                                                    {appointment.groups.map(group => (
                                                        <span key={group.id} className="tag tag-group">
                                                            {group.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {appointment.participants && appointment.participants.length > 0 && (
                                            <div className="appointment-participants">
                                                <strong>Teilnehmer:</strong>
                                                <div className="tags">
                                                    {appointment.participants.map(person => (
                                                        <span key={person.id} className="tag tag-person">
                                                            {getPersonName(person)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {appointment.notes && (
                                            <p><strong>Notizen:</strong> {appointment.notes}</p>
                                        )}
                                    </div>

                                    <div className="appointment-actions">
                                        <button onClick={() => handleEdit(appointment)} className="btn btn-sm btn-edit">
                                            Bearbeiten
                                        </button>
                                        <button onClick={() => handleDelete(appointment.id)}
                                                className="btn btn-sm btn-delete">
                                            Löschen
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AppointmentList;

